import { useEffect, useState, type ReactNode } from "react";
import { Check, Copy, Mail, MessageSquare, Share } from "lucide-react";

import { HomeFeatureCard } from "./HomeFeatureCard";

import type { MyReferralResponse } from "@/types/api";
import { track } from "@/lib/analytics";

export type ReferralSurface = "home" | "referral_page";

interface Props {
  referral: MyReferralResponse;
  // Where the card is rendered — breaks referral_* events down by placement.
  surface: ReferralSurface;
}

/**
 * PostHog events (see the Refer-a-Friend PRD):
 *   referral_card_viewed      { surface }                 — impression, once per mount
 *   referral_share_clicked    { surface, channel }        — any share action tapped
 *   referral_share_completed  { surface, channel }        — native sheet: user picked
 *                                                           an app; copy: link landed
 *                                                           on the clipboard
 * channel ∈ 'sms' | 'email' | 'native' | 'copy'.
 */
export function MemberCodeCard({ referral, surface }: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    track("referral_card_viewed", { surface });
  }, [surface]);

  const shareText = `Get $20 off your first order at System Labs with my code ${referral.code}.`;
  const shareMessage = `${shareText} ${referral.referral_link}`;
  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  function flashCopied() {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 4000);
    track("referral_share_completed", { surface, channel: "copy" });
  }

  // Clipboard API first; when it's unavailable or denied (embedded webviews,
  // some in-app browsers) fall back to the legacy execCommand path so the
  // button never no-ops silently.
  async function onCopy() {
    track("referral_share_clicked", { surface, channel: "copy" });
    try {
      await navigator.clipboard.writeText(referral.referral_link);
      flashCopied();
      return;
    } catch {
      /* fall through */
    }
    const ta = document.createElement("textarea");
    ta.value = referral.referral_link;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    document.body.removeChild(ta);
    if (ok) flashCopied();
  }

  // Native share sheet (iOS/Android + capable desktops). The button is only
  // rendered where the Web Share API exists, so no fallback is needed here.
  async function onNativeShare() {
    track("referral_share_clicked", { surface, channel: "native" });
    try {
      await navigator.share({
        title: "System Labs",
        text: shareText,
        url: referral.referral_link,
      });
      // Resolves only when the user picked a target in the sheet.
      track("referral_share_completed", { surface, channel: "native" });
    } catch {
      // User dismissed the sheet or it failed — no-op (don't surprise-copy).
    }
  }

  // sms: and mailto: hand off to the OS default app; both pre-fill the message.
  const smsHref = `sms:?&body=${encodeURIComponent(shareMessage)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(
    "$20 off your first System Labs order",
  )}&body=${encodeURIComponent(shareMessage)}`;

  return (
    <HomeFeatureCard
      label="Member Code"
      meta={referral.code}
      actions={
        <>
          <ShareAction
            href={smsHref}
            label="Text"
            icon={<MessageSquare size={16} strokeWidth={1.5} />}
            onClick={() =>
              track("referral_share_clicked", { surface, channel: "sms" })
            }
          />
          <ShareAction
            href={mailHref}
            label="Email"
            icon={<Mail size={16} strokeWidth={1.5} />}
            onClick={() =>
              track("referral_share_clicked", { surface, channel: "email" })
            }
          />
          {canNativeShare && (
            <ShareAction
              onClick={onNativeShare}
              label="Share"
              icon={<Share size={16} strokeWidth={1.5} />}
            />
          )}
        </>
      }
    >
      <div className="px-15 pt-15 pb-15 flex flex-col gap-10">
        {/* TODO(referral-experience): $20/$20 is the new two-sided offer.
            Backend still serves friend_discount_label="$25 off" and a tiered
            ladder capped at $40 — wire the amounts through once the backend
            program is updated. */}
        <h2 className="font-mono uppercase text-24 leading-110 text-white">
          Give $20 off,
          <br />
          get $20 off
        </h2>
        <p className="font-body text-15 text-white/75">
          Friends get $20 off their first order.
          <br />
          You earn $20 for every treatment that ships.
        </p>
      </div>

      {/* Link row with inline Copy. The raw link is always visible +
          selectable so there's a manual path even where clipboard / share /
          protocol handlers are blocked. */}
      <div className="flex items-stretch border-t border-[#5D5D5D] bg-black">
        <div className="flex-1 min-w-0 flex items-center gap-10 px-15 py-12">
          <span className="font-mono text-11 uppercase text-white/50 leading-110 shrink-0">
            Your link
          </span>
          <span
            className="font-mono text-12 text-white truncate select-all"
            title={referral.referral_link}
          >
            {referral.referral_link.replace(/^https?:\/\//, "")}
          </span>
        </div>
        <button
          type="button"
          onClick={onCopy}
          aria-label={copied ? "Link copied" : "Copy link"}
          className="shrink-0 flex items-center gap-8 px-15 border-l border-[#5D5D5D] font-mono text-11 uppercase leading-100 text-white bg-black hover:bg-[#1a1a1a]"
        >
          {copied ? (
            <Check size={14} strokeWidth={1.5} />
          ) : (
            <Copy size={14} strokeWidth={1.5} />
          )}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
    </HomeFeatureCard>
  );
}

const shareActionClass =
  "flex-1 basis-0 flex flex-col items-center justify-center gap-8 py-15 font-mono text-11 uppercase leading-100 text-white bg-black hover:bg-[#1a1a1a] border-l border-[#5D5D5D] first:border-l-0";

function ShareAction({
  label,
  icon,
  href,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  href?: string;
  onClick?: () => void;
}) {
  if (href) {
    return (
      <a href={href} onClick={onClick} className={shareActionClass}>
        {icon}
        <span>{label}</span>
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={shareActionClass}>
      {icon}
      <span>{label}</span>
    </button>
  );
}
